import React from 'react';

import { CFG_ANIM } from '../../../../theme/configurator-tokens';
import { useIframeReveal } from '../../lib/iframePreview';
import { LAPTOP_FRAME_DEFAULT, LAPTOP_FRAME_FINISH } from '../../steps/finish/components/DevicePreview/frame-art';
import type { ColorOption } from '../../lib/domain-types';
import type { DeviceMode } from '../wizard/DeviceSwitcher';
import { MacDevice } from './MacDevice';
import { DeviceGhost, MorphDevice } from './MorphDevice';
import { PhoneDevice } from './PhoneDevice';
import { usePreviewStage } from './PreviewStageContext';
import type { AnchorMode, AnchorRegistration, DeviceRole, Rect } from './types';
import { Layer, StepFade, ParkSlide, STAGE_Z, SwitchSlide } from './PreviewStage.styles';

/** Number of animation frames to keep re-measuring an anchor after a registry change,
 *  so the settled rect is captured even after an entrance slide (~STEP_SLIDE_MS). A
 *  bounded burst, NOT a permanent chase loop — it idles once the layout settles. */
const MEASURE_BURST_FRAMES = 42;

/** Horizontal travel of the show/hide slide (px): exit left, enter from the right. */
const SLIDE_PX = 50;

const ROLE_OF: Record<DeviceMode, DeviceRole> = { mobile: 'phone', desktop: 'mac' };

/** Which side of the stage each device parks on, matching the old two-device cross-slide
 *  (`PreviewPane`'s phone anchor took parkDir -1, its mac anchor +1). */
const PARK_DIR: Record<DeviceMode, 1 | -1> = { mobile: -1, desktop: 1 };

/** Per-role placement the host derives each render from the registry + live toggles. */
interface Placement {
  rect: Rect | null;
  /** The role is on the current surface (drives the outer show/hide slide + fade). */
  present: boolean;
  /** Single-mode device-switch off state (drives the inner SLIDE). */
  parked: boolean;
  parkDir: 1 | -1;
  /** Which surface this role is on, or null when absent (gates the slide transition). */
  mode: AnchorMode | null;
  bare: boolean;
  finishFrame: boolean;
  zoomWidth?: number;
  interactive: boolean;
  /** Cut the floated device to the surface's clip pane (drives the host's clip-path). */
  clip: boolean;
  z: number;
}

const ABSENT: Placement = {
  rect: null,
  present: false,
  parked: false,
  parkDir: 1,
  mode: null,
  bare: false,
  finishFrame: false,
  interactive: false,
  clip: false,
  z: 1,
};

function readRect(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

/** A measured, laid-out rect (non-null and non-zero). */
function validRect(r: Rect | null): r is Rect {
  return r != null && r.width > 0 && r.height > 0;
}

function rectsEqual(a: Rect | null, b: Rect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

/**
 * A `clip-path: inset(...)` cutting a device's positioned box down to the clip pane
 * (both viewport-space). Insets are measured from the box's own edges, so the cut
 * lands exactly on the panel edge — letting a device sized to bleed past its panel
 * (the finish laptop) be clipped there instead of at the viewport edge. Returns
 * undefined when the box sits fully inside the pane (no cut needed) or either rect is
 * missing. Applied to the StepFade: the entrance fade (opacity 0 at the slid-out
 * position) masks the brief moment the moving clip lags the panel, so the cut reads
 * as static at rest. */
function clipInset(box: Rect | null, clip: Rect | null): string | undefined {
  if (!box || !clip) return undefined;
  const top = Math.max(0, clip.top - box.top);
  const left = Math.max(0, clip.left - box.left);
  const right = Math.max(0, box.left + box.width - (clip.left + clip.width));
  const bottom = Math.max(0, box.top + box.height - (clip.top + clip.height));
  if (top < 0.5 && right < 0.5 && bottom < 0.5 && left < 0.5) return undefined;
  return `inset(${top}px ${right}px ${bottom}px ${left}px)`;
}

/** The transform/opacity of one role's show/hide slide. */
interface SlideFade {
  tx: number;
  opacity: number;
  animate: boolean;
}

/**
 * Drives one role's entrance / exit as a horizontal SLIDE + FADE — and nothing else,
 * so it can't flicker on the host's rect-settle measurements (those only move the
 * INSTANT position, never this). `ready` = the role is on the current surface AND has
 * a measured rect to sit at:
 * - ready false → true: snap to +SLIDE_PX / opacity 0 (no transition), then animate to
 *   rest → slides IN from the right.
 * - true → false: animate to -SLIDE_PX / opacity 0 → slides OUT to the left.
 */
function useSlideFade(ready: boolean): SlideFade {
  const [s, setS] = React.useState<SlideFade>({ tx: SLIDE_PX, opacity: 0, animate: false });
  const prevReady = React.useRef(false);
  const raf = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    const was = prevReady.current;
    prevReady.current = ready;
    if (raf.current) cancelAnimationFrame(raf.current);
    if (ready && !was) {
      // ENTER from the right: park at +SLIDE_PX (no transition), then animate to rest
      // next frame so the browser paints the start offset first.
      setS({ tx: SLIDE_PX, opacity: 0, animate: false });
      raf.current = requestAnimationFrame(() => {
        raf.current = requestAnimationFrame(() => setS({ tx: 0, opacity: 1, animate: true }));
      });
    } else if (!ready && was) {
      // EXIT to the left.
      setS({ tx: -SLIDE_PX, opacity: 0, animate: true });
    }
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [ready]);

  return s;
}

export interface PreviewStageViewProps {
  /** The live site URL. The morph iframe's `src` always; the two role iframes' `src` only
   *  once they are lazily loaded. A change is the only reload. */
  previewUrl: string;
  /** Selected brand colour (tints the bezels' strips + screen base). */
  color: ColorOption | null;
  /** Desktop only: the host renders nothing on mobile (separate experience). */
  isMobile: boolean;
}

/**
 * The persistent preview host (presentational core). Mounted once at provider scope
 * (above the route Outlet) so it survives every step navigation. It owns EXACTLY
 * THREE `<iframe>` elements — one phone-role, one mac-role, and one unified `morph`
 * — authored once and never re-parented, so no surface ever reloads one.
 *
 * The role (mac/phone) iframes serve `group` mode (the Finish step's laptop+phone,
 * shown together) — the only place two devices are visible at once, which a single
 * iframe fundamentally can't do — and there only as the live fallback when no static
 * screenshot exists. They are therefore loaded LAZILY: no `src` until a role anchor is
 * actually placed, so the build steps and the screenshot Finish fetch the site once (the
 * morph), never three times. Every OTHER surface — the build steps' `single`
 * mode switcher AND the full-screen Live Preview — floats the SAME warm `morph`
 * iframe instead of a role one, specifically so whatever route/session state the
 * user reaches while configuring is still there when they open Live Preview: the
 * overlay is not a separate, never-interacted-with iframe. `stageReg` picks which
 * anchor (build-step vs. full-screen) currently owns that one device.
 *
 * Two motion layers per device: the OUTER slides+fades the device on/off as it
 * appears or leaves the canvas (out to the left, in from the right) while its
 * position tracks the anchor INSTANTLY (no scale, no flicker); the INNER keeps the
 * device-switcher cross-SLIDE within a single step. Surfaces render only empty, sized
 * anchor boxes (see {@link useDeviceAnchor}); the host measures each + floats the
 * matching device over it. Kept store-free (its data source is the thin
 * `PreviewStage` wrapper) so the iframe-count + no-reload invariants can be
 * component-tested without the Redux configurator store.
 */
export const PreviewStageView = ({ previewUrl, color, isMobile }: PreviewStageViewProps): React.ReactElement | null => {
  const { registrations, registryVersion, device, fullscreenOpen, fullscreenDevice, setContainerNode, clipNode } =
    usePreviewStage();

  // While full-screen is open, the overlay's OWN Mobile/Desktop toggle
  // (`fullscreenDevice`) stands in for the build-step `device` — it's what the morph
  // device actually renders, what its resize-cover reflow-masks on a change, AND what
  // the measure effects below must re-measure on (the full-screen anchor resizes with
  // `fullscreenDevice`, not `device`, since it's a SEPARATE anchor from the build-step
  // one). The ghost cross-slide state further down intentionally stays keyed on the
  // build-step `device` only: it animates that surface's own switcher, which the user
  // cannot even reach while the overlay covers it.
  const effectiveDevice = fullscreenOpen ? fullscreenDevice : device;

  // The two role iframes (mac/phone) exist ONLY for the Finish step's both-devices-at-
  // once group — and there, only as the live FALLBACK shown when no static screenshot
  // exists for the selection. So don't load the (heavy) preview site into them until a
  // role anchor is actually placed on a surface: through the build steps and the normal
  // screenshot Finish they carry no `src` and never fetch the site — only the morph does
  // (one load, not three). A LATCH keeps the never-reload invariant intact: once a role
  // has been placed its src stays set, so the iframe loads at most once and is never
  // cleared — it just starts later. The morph always loads (build steps + Live Preview).
  const liveRegs = registrations();
  const macNeedsSrc = !fullscreenOpen && liveRegs.some((r) => r.spec.role === 'mac' && !r.spec.unified);
  const phoneNeedsSrc = !fullscreenOpen && liveRegs.some((r) => r.spec.role === 'phone' && !r.spec.unified);
  const roleLoadedRef = React.useRef<Record<DeviceRole, boolean>>({ phone: false, mac: false });
  if (macNeedsSrc) roleLoadedRef.current.mac = true;
  if (phoneNeedsSrc) roleLoadedRef.current.phone = true;
  const macSrc = roleLoadedRef.current.mac ? previewUrl : '';
  const phoneSrc = roleLoadedRef.current.phone ? previewUrl : '';

  // One reveal gate per role, keyed on its (possibly-empty) src: a role reveals only once
  // its OWN iframe loads, and RE-gates the moment a lazy role first gets a src, so the
  // brand placeholder covers that fresh load instead of flashing the blank frame. Both
  // roles frame the SAME url once loaded, so each gate holds its own iframe's ref to
  // recognise its own `page-rendered` message. The morph is keyed on previewUrl directly
  // (its src never goes empty), so navigation / a device toggle never restarts its settle.
  const phoneReveal = useIframeReveal(phoneSrc);
  const macReveal = useIframeReveal(macSrc);
  const morphReveal = useIframeReveal(previewUrl);

  // Measured anchor rects (viewport space). Lives in host state — updating it never
  // re-renders the steps, only this host.
  const [rects, setRects] = React.useState<Map<number, Rect>>(new Map());
  const rectsRef = React.useRef(rects);
  rectsRef.current = rects;

  // The current surface's clip pane rect (viewport space), re-measured in the same
  // burst as the anchors so a clipped device's cut tracks the panel on layout/resize.
  const [clipRect, setClipRect] = React.useState<Rect | null>(null);
  const clipRectRef = React.useRef(clipRect);
  clipRectRef.current = clipRect;

  const measure = React.useCallback((): void => {
    const regs = registrations();
    const next = new Map<number, Rect>();
    let changed = regs.length !== rectsRef.current.size;
    for (const reg of regs) {
      const rect = readRect(reg.element);
      next.set(reg.id, rect);
      if (!changed && !rectsEqual(rectsRef.current.get(reg.id) ?? null, rect)) changed = true;
    }
    if (changed) setRects(next);

    const clip = clipNode ? readRect(clipNode) : null;
    if (!rectsEqual(clipRectRef.current, clip)) setClipRect(clip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, clipNode]);

  // Re-measure in a bounded burst that idles once layout settles (no permanent
  // per-frame chase). One burst restarts/cancels the prior via this ref.
  const burstRaf = React.useRef(0);
  const startBurst = React.useCallback((): void => {
    cancelAnimationFrame(burstRaf.current);
    let frame = 0;
    const tick = (): void => {
      measure();
      frame += 1;
      if (frame < MEASURE_BURST_FRAMES) burstRaf.current = requestAnimationFrame(tick);
    };
    burstRaf.current = requestAnimationFrame(tick);
  }, [measure]);

  // Burst whenever the set of anchors changes (a step navigation re-registers them),
  // so the SETTLED rect is captured even while the new step's entrance slide is still
  // in flight.
  React.useEffect(() => {
    startBurst();
    return () => cancelAnimationFrame(burstRaf.current);
  }, [registryVersion, startBurst]);

  // A viewport resize re-settles every region. This is ALSO a burst (not a single
  // pass): a surface may reflow its anchor in response to the resize via its own
  // ResizeObserver → state → re-render (the finish centring shift, the build pane's
  // sizing), which lands a frame or two AFTER the resize event — a single synchronous
  // measure would read the pre-reflow position and leave the floated device offset.
  React.useEffect(() => {
    const onResize = (): void => startBurst();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [startBurst]);

  // A Mobile/Desktop toggle — the build-step one OR the full-screen overlay's own —
  // resizes the active unified anchor (portrait phone box <-> landscape mac box, or the
  // overlay's Clip) without changing the registry or the window, so neither measure
  // trigger above fires. Keyed on `effectiveDevice` (not the raw build-step `device`)
  // specifically so the overlay's own switcher re-measures its Clip anchor too — that
  // anchor resizes with `fullscreenDevice`, which `device` alone would miss entirely.
  //
  // Measure it SYNCHRONOUSLY, before paint. The anchor's new size is already in the DOM
  // by the time a layout effect runs (React flushes the whole commit first), so this
  // reads the post-switch box and re-renders the stage at it without ever painting the
  // old one. Deferring to the burst below would hand the morph device a box belonging to
  // the previous device for a frame — and it sizes the cross-origin iframe from that box
  // (see MorphDevice's useLayoutEffect), so the framed site would lay out twice.
  React.useLayoutEffect(() => {
    measure();
  }, [effectiveDevice, measure]);

  // The burst still runs, for what a single synchronous pass cannot catch: the pane's own
  // ResizeObserver-driven reflow landing a frame or two later.
  React.useEffect(() => {
    startBurst();
    return () => undefined;
  }, [effectiveDevice, startBurst]);

  // Snapshot the registry whenever it mutates (the version bump is the real trigger;
  // `registrations` itself is stable, so it must be paired with the version).
  const regs = React.useMemo(
    () => registrations(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registrations, registryVersion],
  );

  // Derive, per role, the single registration to honour + the resulting placement.
  const placements = React.useMemo<Record<DeviceRole, Placement>>(() => {
    const forRole = (role: DeviceRole): Placement => {
      // Full-screen now exclusively floats the unified morph device (see `stageReg`
      // below); the mac/phone role devices never show behind the blurred backdrop.
      if (fullscreenOpen) return ABSENT;

      const roleRegs = regs.filter((r) => r.spec.role === role && !r.spec.unified);
      if (roleRegs.length === 0) return ABSENT;

      const reg: AnchorRegistration | undefined =
        roleRegs.find((r) => r.spec.mode === 'group') ?? roleRegs.find((r) => r.spec.mode === 'single');
      if (!reg) return ABSENT;
      // Single mode parks the non-active device (the inner slide); group shows both.
      const parked = reg.spec.mode === 'single' && ROLE_OF[device] !== role;
      return {
        rect: rects.get(reg.id) ?? null,
        present: true,
        parked,
        parkDir: reg.spec.parkDir ?? 1,
        mode: reg.spec.mode,
        bare: false,
        finishFrame: reg.spec.bezel === 'macFinish',
        zoomWidth: reg.spec.phoneZoomWidth,
        // The on-stage build-step preview (single mode, not parked) accepts the wheel so
        // the site inside it can be scrolled. The PARKED device stays inert so its
        // invisible, off-axis frame can never catch the cursor; group (finish) + bare keep
        // their prior non-interactive behaviour. This is pure pointer-events: it never
        // touches the StepFade / ParkSlide transforms, so the slide transitions are
        // unaffected.
        interactive: reg.spec.mode === 'single' && !parked,
        clip: reg.spec.clip ?? false,
        z: 1,
      };
    };

    return { phone: forRole('phone'), mac: forRole('mac') };
  }, [regs, rects, device, fullscreenOpen]);

  // The UNIFIED stage device (one morphing iframe): the build-step single-mode
  // anchor normally, or — while the overlay is open — the full-screen anchor
  // PreviewOverlay registers instead, so opening Live Preview floats the SAME warm
  // device (and whatever in-preview route/session it already carries) rather than a
  // separate role iframe that was never interacted with. Derived separately from the
  // two physical roles so the delicate per-role park/held-rect machinery is untouched.
  const stageReg = React.useMemo(
    () => regs.find((r) => r.spec.unified && r.spec.mode === (fullscreenOpen ? 'fullscreen' : 'single')),
    [regs, fullscreenOpen],
  );

  const stageRectRaw = stageReg ? rects.get(stageReg.id) ?? null : null;
  const heldStageRef = React.useRef<Rect | null>(null);
  if (validRect(stageRectRaw)) heldStageRef.current = stageRectRaw;
  const stageRect = heldStageRef.current;
  const stagePresent = stageReg != null;
  const stageSlideFade = useSlideFade(stagePresent && validRect(stageRectRaw));

  // ---- The Mobile / Desktop cross-slide, on ONE warm iframe ------------------------
  // The old stage parked one of two devices off-axis and slid the other in. The unified
  // stage has a single device, so the incoming one is the real (morphed) device sliding
  // in from its park side while the outgoing one is a GHOST — bezel + brand fill, no
  // iframe — pinned at the rect the stage occupied BEFORE the switch and sliding out to
  // its own park side. Same tokens, same motion.
  //
  // `snap` is entered DURING RENDER (never from an effect) so the incoming device is
  // already parked off-axis at opacity 0 in the same commit that resizes it: an effect
  // would let the browser paint it at rest, at the new size, for one frame first.
  const [switchFrom, setSwitchFrom] = React.useState(device);
  const [switchPhase, setSwitchPhase] = React.useState<'idle' | 'snap' | 'run'>('idle');
  const [ghost, setGhost] = React.useState<{ device: DeviceMode; rect: Rect } | null>(null);

  if (switchFrom !== device) {
    setSwitchFrom(device);
    setSwitchPhase('snap');
    // `stageRect` is still the pre-switch rect here (the re-measure burst runs a frame
    // later), which is exactly the box the outgoing device must be pinned to.
    setGhost(validRect(stageRect) ? { device: switchFrom, rect: stageRect } : null);
  }

  // Paint the parked start offset, THEN animate to rest (double rAF, as useSlideFade).
  React.useEffect(() => {
    if (switchPhase !== 'snap') return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setSwitchPhase('run'));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [switchPhase]);

  // Retire the ghost once the slide has landed. A switch that lands mid-slide re-enters
  // `snap`, which cancels this and restarts the run.
  React.useEffect(() => {
    if (switchPhase !== 'run') return undefined;
    const timer = window.setTimeout(() => {
      setSwitchPhase('idle');
      setGhost(null);
    }, CFG_ANIM.DEVICE_SWITCH_MS);
    return () => window.clearTimeout(timer);
  }, [switchPhase, ghost]);

  // The ghost is pinned to a rect frozen at switch time, so it cannot follow the stage.
  // If the anchor unregisters while the slide is still running (the user toggles device,
  // then leaves the build steps inside DEVICE_SWITCH_MS), retire it: the morph beside it
  // fades out with the stage, but the ghost would keep fading in place over the next
  // screen. Rendering is gated on `stagePresent` too, so no frame paints before this runs.
  React.useEffect(() => {
    if (stagePresent) return;
    setSwitchPhase('idle');
    setGhost(null);
  }, [stagePresent]);

  const parked = switchPhase === 'snap';
  const switchAnimate = switchPhase === 'run';
  const switchTx = parked ? PARK_DIR[device] * CFG_ANIM.DEVICE_SWITCH_OFFSET_PX : 0;
  const ghostTx = ghost && !parked ? PARK_DIR[ghost.device] * CFG_ANIM.DEVICE_SWITCH_OFFSET_PX : 0;

  // Hold the last VALID (non-zero) rect per role so a transient unmeasured/zero frame
  // never collapses the device; the position is applied instantly (never animated).
  const heldRef = React.useRef<Record<DeviceRole, Rect | null>>({ phone: null, mac: null });
  const holdRect = (role: DeviceRole): Rect | null => {
    const r = placements[role].rect;
    if (validRect(r)) heldRef.current[role] = r;
    return heldRef.current[role];
  };
  const macRect = holdRect('mac');
  const phoneRect = holdRect('phone');

  // Hold the inner PARK state when a role goes absent. In single mode only ONE device
  // is on stage (the other is parked = invisible via the inner layer); without this,
  // a step change resets the parked device to "neutral" and it would light up as it
  // slid off. Holding the last value keeps the inactive device hidden through the
  // exit, so ONLY the active device slides out/in. A group role (finish) holds
  // parked = false, so it keeps both devices sliding — unchanged.
  const heldParkedRef = React.useRef<Record<DeviceRole, boolean>>({ phone: false, mac: false });
  const parkedOf = (role: DeviceRole): boolean => {
    if (placements[role].present) heldParkedRef.current[role] = placements[role].parked;
    return placements[role].present ? placements[role].parked : heldParkedRef.current[role];
  };
  const macParked = parkedOf('mac');
  const phoneParked = parkedOf('phone');

  // Show/hide slide + fade per role. `ready` gates on the FRESH rect (not the held
  // one) so the entrance only fires once THIS surface's position is measured — never
  // a slide-in at a stale position. While present + stable the fresh rect stays
  // non-null, so this can't toggle mid-surface (no flicker); the position below still
  // uses the held rect so a transient frame never collapses a visible device.
  const macSlideFade = useSlideFade(placements.mac.present && validRect(placements.mac.rect));
  const phoneSlideFade = useSlideFade(placements.phone.present && validRect(placements.phone.rect));

  // Enable the inner device-switch SLIDE only while STAYING in single mode; a surface
  // change snaps it (transition off) so the outer slide owns that transition.
  const prevModeRef = React.useRef<Record<DeviceRole, AnchorMode | null>>({ phone: null, mac: null });
  const macSlide = placements.mac.mode === 'single' && prevModeRef.current.mac === 'single';
  const phoneSlide = placements.phone.mode === 'single' && prevModeRef.current.phone === 'single';
  React.useEffect(() => {
    prevModeRef.current = { phone: placements.phone.mode, mac: placements.mac.mode };
  }, [placements.phone.mode, placements.mac.mode]);

  // Raise the whole layer for the full-screen overlay, and (less far) for any surface
  // that elevates because its preview sits on an opaque panel, e.g. the success screen.
  // An elevating surface must clear that panel WITHOUT covering the UserMenu, whose
  // account popover opens over the devices; only the overlay is allowed above it.
  const layerZ = fullscreenOpen
    ? STAGE_Z.fullscreen
    : regs.some((r) => r.spec.elevate)
      ? STAGE_Z.elevated
      : STAGE_Z.resting;

  if (isMobile) return null;

  // Interpolated raw into styled-components declarations below. Safe because
  // `toColor()` sanitizes every ColorOption paint value as it leaves the API (rejects
  // anything that could break out of a CSS declaration; see lib/color.ts's
  // sanitizeCssPaint) and is the sole producer, so nothing unsanitized reaches here.
  const topColor = color?.topNav;
  const bottomColor = color?.bottomNav;
  const bgColor = color?.bg;

  const outerStyle = (rect: Rect | null, sf: SlideFade, clip: boolean): React.CSSProperties => {
    const box = rect ?? { left: 0, top: 0, width: 0, height: 0 };
    const inset = clip ? clipInset(rect, clipRect) : undefined;
    return {
      left: box.left,
      top: box.top,
      width: box.width,
      height: box.height,
      opacity: sf.opacity,
      transform: `translate3d(${sf.tx}px, 0, 0)`,
      clipPath: inset,
      WebkitClipPath: inset,
    };
  };

  return (
    <Layer ref={setContainerNode} data-cfg-layer="" $z={layerZ}>
      <StepFade
        data-cfg-stage="mac"
        style={outerStyle(macRect, macSlideFade, placements.mac.clip)}
        $interactive={placements.mac.interactive && macSlideFade.opacity > 0}
        $z={placements.mac.z}
        $animate={macSlideFade.animate}
      >
        <ParkSlide $parked={macParked} $parkDir={placements.mac.parkDir} $slide={macSlide}>
          <MacDevice
            src={macSrc}
            topColor={topColor}
            bgColor={bgColor}
            frame={placements.mac.finishFrame ? LAPTOP_FRAME_FINISH : LAPTOP_FRAME_DEFAULT}
            bare={placements.mac.bare}
            shown={macReveal.revealed}
            onLoad={macReveal.onLoad}
            iframeRef={macReveal.iframeRef}
          />
        </ParkSlide>
      </StepFade>

      <StepFade
        data-cfg-stage="phone"
        style={outerStyle(phoneRect, phoneSlideFade, placements.phone.clip)}
        $interactive={placements.phone.interactive && phoneSlideFade.opacity > 0}
        $z={placements.phone.z}
        $animate={phoneSlideFade.animate}
      >
        <ParkSlide $parked={phoneParked} $parkDir={placements.phone.parkDir} $slide={phoneSlide}>
          <PhoneDevice
            src={phoneSrc}
            topColor={topColor}
            bottomColor={bottomColor}
            bgColor={bgColor}
            zoomWidth={placements.phone.zoomWidth}
            bare={placements.phone.bare}
            shown={phoneReveal.revealed}
            onLoad={phoneReveal.onLoad}
            iframeRef={phoneReveal.iframeRef}
          />
        </ParkSlide>
      </StepFade>

      {ghost && stagePresent ? (
        <StepFade
          data-cfg-stage="morph-ghost"
          style={{ left: ghost.rect.left, top: ghost.rect.top, width: ghost.rect.width, height: ghost.rect.height }}
          $interactive={false}
          $z={1}
          $animate={false}
        >
          <SwitchSlide $tx={ghostTx} $opacity={parked ? 1 : 0} $animate={switchAnimate}>
            <DeviceGhost device={ghost.device} topColor={topColor} bottomColor={bottomColor} bgColor={bgColor} />
          </SwitchSlide>
        </StepFade>
      ) : null}

      <StepFade
        data-cfg-stage="morph"
        style={outerStyle(stageRect, stageSlideFade, false)}
        $interactive={stagePresent && stageSlideFade.opacity > 0}
        // 53: matches the sibling-relative z the old role-based fullscreen path used
        // (arbitrary, just higher than the resting mac/phone/ghost siblings at z<=2).
        $z={stageReg?.spec.mode === 'fullscreen' ? 53 : 2}
        $animate={stageSlideFade.animate}
      >
        <SwitchSlide $tx={switchTx} $opacity={parked ? 0 : 1} $animate={switchAnimate}>
          <MorphDevice
            src={previewUrl}
            device={effectiveDevice}
            topColor={topColor}
            bottomColor={bottomColor}
            bgColor={bgColor}
            shown={morphReveal.revealed}
            onLoad={morphReveal.onLoad}
            iframeRef={morphReveal.iframeRef}
            bare={stageReg?.spec.mode === 'fullscreen'}
          />
        </SwitchSlide>
      </StepFade>
    </Layer>
  );
};

export default PreviewStageView;
